import React from 'react';
import { useTranslation } from 'react-i18next';
import { SearchX, Plus, HelpCircle, Sparkles, Shield, Users } from 'lucide-react';

interface SearchEmptyStateProps {
  query: string;
  onQuickCreate?: (scope: 'personal' | 'household') => void;
  // 🛡️ Hybrid Scoping Props
  currentHouseholdId: string | null;
  activeScope: 'PERSONAL' | 'SHARED' | 'BOTH';
}

/**
 * ⚡ SearchEmptyState
 * Refactored for Hybrid Scoping: Clearly distinguishes between personal and shared search results.
 */
export const SearchEmptyState: React.FC<SearchEmptyStateProps> = ({ 
  query, 
  onQuickCreate,
  currentHouseholdId,
  activeScope
}) => {
  const { t } = useTranslation();

  // 🛡️ Determine the context labels based on the active search scope
  const isPersonalOnly = !currentHouseholdId || activeScope === 'PERSONAL';
  const isSharedOnly = currentHouseholdId && activeScope === 'SHARED';

  return (
    <div className="py-20 flex flex-col items-center justify-center text-center px-6 animate-in fade-in zoom-in-95 duration-500">
      {/* 🔍 Icon Anchor */}
      <div className="relative mb-6">
        <div className={`absolute inset-0 blur-3xl rounded-full ${isPersonalOnly ? 'bg-emerald-500/10' : 'bg-indigo-500/10'}`} />
        <div className="relative p-6 bg-slate-50 dark:bg-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-inner">
          <SearchX className="w-10 h-10 text-slate-300 dark:text-slate-600" />
        </div>
        <div className="absolute -bottom-2 -right-2 p-2 bg-white dark:bg-slate-950 rounded-full border border-slate-100 dark:border-slate-800 shadow-lg">
          {isPersonalOnly ? (
            <Shield className="w-4 h-4 text-emerald-500" />
          ) : (
            <Users className="w-4 h-4 text-indigo-500" />
          )}
        </div>
      </div>

      {/* ✍️ Text Content */}
      <div className="max-w-xs space-y-2">
        <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">
          {t('search.no_results.title', 'Zero Matches Found')}
        </h3>
        <p className="text-[11px] font-medium text-slate-400 leading-relaxed">
          {t('search.no_results.desc', "We couldn't find anything matching")} 
          <span className={`${isPersonalOnly ? 'text-emerald-500' : 'text-indigo-500'} font-black italic mx-1`}>
            "{query}"
          </span> 
          {isPersonalOnly 
            ? t('search.no_results.personal_suffix', 'in your personal partition.')
            : t('search.no_results.shared_suffix', 'in the household hub.')}
        </p>
      </div>

      {/* ⚡ Action Suggestions */}
      <div className="mt-10 grid grid-cols-1 gap-2 w-full max-w-[240px]">
        {/* If searching 'BOTH', we offer creation in the current default scope */}
        <button 
          onClick={() => onQuickCreate?.(activeScope === 'SHARED' ? 'household' : 'personal')}
          className={`flex items-center justify-center gap-2 p-3 text-white rounded-2xl transition-all shadow-lg active:scale-95 ${
            isPersonalOnly ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20' : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-500/20'
          }`}
        >
          <Plus className="w-4 h-4" />
          <span className="text-[10px] font-black uppercase tracking-widest">
            {t('search.create_new', 'Create in')} {isPersonalOnly ? 'Personal' : 'Household'}
          </span>
        </button>
        
        <div className="flex items-center gap-2 p-3 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 opacity-60">
          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
          <span className="text-[9px] font-bold text-slate-500 uppercase tracking-tighter">
            {isPersonalOnly 
              ? t('search.tip.personal', 'Try searching for private tasks or assets')
              : t('search.tip.shared', 'Try searching for shared tasks or members')}
          </span>
        </div>
      </div>
    </div>
  );
};